import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ContactPage: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDarkMode = theme === 'dark';
  
  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                className="h-8 w-auto mr-2" 
                src="/favicon.png" 
                alt="FlowCXP Logo" 
              />
              <span className={`text-xl font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{t('common.appName')}</span>
            </Link>
            <Link 
              to="/" 
              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md ${
                isDarkMode 
                  ? 'text-gray-300 hover:bg-gray-700' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t('navigation.backToHome')}
            </Link>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className={`text-3xl sm:text-4xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('pages.contact.title')}
          </h1>
          <p className={`mt-4 text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {t('pages.contact.subtitle')}
          </p>
        </div>
        
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow overflow-hidden rounded-lg`}>
          <div className="px-4 py-5 sm:p-6">
            <div className="mx-auto max-w-lg">
              {/* Contact Information */}
              <div>
                <h2 className={`text-2xl font-bold mb-8 text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('pages.contact.contactInfo')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg shadow-md text-white">
                    <h3 className="text-lg font-medium mb-2">
                      {t('common.email')}
                    </h3>
                    <p className="text-xl font-semibold">
                      <a href="mailto:sales@flowcxp.com" className="hover:underline">sales@flowcxp.com</a>
                    </p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-6 rounded-lg shadow-md text-white">
                    <h3 className="text-lg font-medium mb-2">
                      {t('common.phone')}
                    </h3>
                    <p className="text-xl font-semibold">
                      <a href="tel:+18005551234" className="hover:underline">+1 (858) 463-5657</a>
                    </p>
                  </div>
                  
                  <div className={`p-6 rounded-lg shadow-md ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`}>
                    <h3 className="text-lg font-medium mb-2">
                      {t('pages.contact.officeHours')}
                    </h3>
                    <p className="text-md">
                      Monday - Friday<br />
                      9:00 AM - 5:00 PM EST
                    </p>
                  </div>
                  
                  <div className={`p-6 rounded-lg shadow-md ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'}`}>
                    <h3 className="text-lg font-medium mb-2">
                      {t('pages.contact.headquarters')}
                    </h3>
                    <p className="text-md">
                      1111b S Governors Avenue<br />
                      STE 37772<br />
                      Dover, DE 19904 US
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
